import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  setHotels,
  setError,
  setLoading,
  clearError,
  resetHotelState,
} from '../../../../../HotelDetails/hotelSlice';
import { useCalcTwoPrices } from '../../../../../../utils/convertEurToUsd';
import { FlightBundle } from '../../../../../../interfaces/flightInterface';
import { useHotelSearchByGeo } from './useHotelSearchAPI';

export const GEO_HOTEL_RADIUS = 30; // 30 km

export const useHotelDataManagementByGeo = ({
  urlSearchParams,
  cheapestFlightData,
  is_dp,
  enabled,
}: {
  urlSearchParams: Record<string, unknown>;
  cheapestFlightData?: FlightBundle | undefined;
  is_dp?: 0 | 1;
  enabled: boolean;
}) => {
  const dispatch = useDispatch();
  const { adjustFiltersPriceRangeLabels } = useCalcTwoPrices();

  const [optionalFilters, setOptionalFilters] = useState<any>(null);
  const [isSwitchPickerSearching, setIsSwitchPickerSearching] = useState(false);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [cachedParamsString, setCachedParamsString] = useState('');

  const relevantParamsString = useMemo(() => {
    return JSON.stringify({
      latitude: urlSearchParams.latitude,
      longitude: urlSearchParams.longitude,
      from: urlSearchParams.from,
      to: urlSearchParams.to,
      adult: urlSearchParams.adult,
      child: urlSearchParams.child,
      infant: urlSearchParams.infant,
      multi_room_guests: urlSearchParams.multi_room_guests,
    });
  }, [urlSearchParams]);

  const {
    data: geoHotelsData,
    isFetching,
    isLoading,
    isSuccess,
    error,
    refetch: refetchGeoHotels,
  } = useHotelSearchByGeo({
    searchParams: urlSearchParams as Record<string, any>,
    latitude: urlSearchParams.latitude as number,
    longitude: urlSearchParams.longitude as number,
    radius: GEO_HOTEL_RADIUS,
    is_dp,
    enabled:
      enabled && !!urlSearchParams.latitude && !!urlSearchParams.longitude,
  });

  // Reset state when key params change (new event selected / dates changed)
  useEffect(() => {
    if (relevantParamsString !== cachedParamsString && relevantParamsString) {
      setIsSwitchPickerSearching(true);
      setIsFullyLoaded(false);
      setOptionalFilters(null);
      dispatch(setLoading(true));
      dispatch(resetHotelState());
      setCachedParamsString(relevantParamsString);
    }
  }, [relevantParamsString, cachedParamsString, dispatch]);

  // Dispatch hotels and optional filters when geo search succeeds
  useEffect(() => {
    if (isSuccess && geoHotelsData) {
      const hotels = geoHotelsData.hotels ?? [];
      dispatch(setHotels(hotels));
      dispatch(setLoading(false));
      setIsSwitchPickerSearching(false);
      setIsFullyLoaded(true);

      if (geoHotelsData.optional_filters?.length) {
        const flightPriceObj = cheapestFlightData?.price;
        const adjustedFilters = adjustFiltersPriceRangeLabels({
          opt_filters: geoHotelsData.optional_filters,
          flight_price: flightPriceObj?.grandTotal || flightPriceObj?.total,
          flight_currency: flightPriceObj?.currency_sign,
        });
        setOptionalFilters(adjustedFilters);
      }
    }
  }, [isSuccess, geoHotelsData, cheapestFlightData, dispatch]);

  // Re-adjust filter price labels when flight data arrives after hotels
  useEffect(() => {
    if (
      isFullyLoaded &&
      geoHotelsData?.optional_filters?.length &&
      cheapestFlightData?.price
    ) {
      const flightPriceObj = cheapestFlightData.price;
      const adjustedFilters = adjustFiltersPriceRangeLabels({
        opt_filters: geoHotelsData.optional_filters,
        flight_price: flightPriceObj?.grandTotal || flightPriceObj?.total,
        flight_currency: flightPriceObj?.currency_sign,
      });
      setOptionalFilters(adjustedFilters);
    }
  }, [cheapestFlightData, isFullyLoaded, geoHotelsData]);

  // Handle errors
  useEffect(() => {
    if (error) {
      dispatch(setError(error.message || 'An error occurred'));
    } else {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  /**
   * Re-applies this hook's own last successful geo response to the Redux hotel
   * slice, undoing a temporary override (a city search, a map "refresh this
   * area") without a network round trip — the geo query holds onto its data
   * (`staleTime` 2 min, `keepPreviousData`), so the pristine radius results are
   * still in hand.
   *
   * Deliberately sourced from the query rather than from a snapshot of Redux:
   * a snapshot taken while an earlier override was showing would restore that
   * override's hotels as if they were the radius search.
   *
   * When nothing is cached it refetches instead and leaves the loader up; the
   * hook's normal success effect then fills Redux.
   */
  const restoreGeoResults = useCallback(() => {
    if (!geoHotelsData) {
      dispatch(setLoading(true));
      refetchGeoHotels();
      return;
    }
    dispatch(setHotels(geoHotelsData.hotels ?? []));
    dispatch(setLoading(false));
    if (geoHotelsData.optional_filters?.length) {
      const flightPriceObj = cheapestFlightData?.price;
      setOptionalFilters(
        adjustFiltersPriceRangeLabels({
          opt_filters: geoHotelsData.optional_filters,
          flight_price: flightPriceObj?.grandTotal || flightPriceObj?.total,
          flight_currency: flightPriceObj?.currency_sign,
        })
      );
    }
  }, [
    geoHotelsData,
    cheapestFlightData,
    adjustFiltersPriceRangeLabels,
    refetchGeoHotels,
    dispatch,
  ]);

  const showInitialLoader =
    enabled &&
    !!urlSearchParams.latitude &&
    !!urlSearchParams.longitude &&
    (isSwitchPickerSearching || isLoading);

  const isFetchingValidData = isFetching;

  return {
    optionalFilters,
    isSwitchPickerSearching,
    showInitialLoader,
    isFetchingValidData,
    isFullyLoaded,
    restoreGeoResults,
  };
};
